import React, { useEffect, useState } from "react";
import {startCase, cloneDeep, toLower, split, replace, slice, size} from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import MobileProductGrid from "./MobileProductGrid";
import DesktopProductGrid from "./DesktopProductGrid";
import useSystemHook from "./../../hooks/SystemHook";

const ProductGrid = ({
                         loading,
                         products,
                         pricingData,
                         stockInfo,
                         customerCategory,
                         categoriesAndTypes,
                         activeVendor,
                         categoryFilters,
                         typeFilters,
                         vendorFilters,
                         makeFilters,
                         modelFilters,
                         variantFilters,
                         yearFilters,
                         activeFilters,
                         pageNumber,
                         pageSize,
                         total,
                         handlePageNumberChange,
                         updateQueryParameters,
                         filtersLoading,
                     }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isMobile, isTablet, isDesktop } = useSystemHook();
    const [productCategories, setProductCategories] = useState([]);
    const [productCategoriesTruncated, setProductCategoriesTruncated] = useState(
        []
    );
    const [productCategoriesListToUse, setProductCategoriesListToUse] = useState(
        []
    );
    const [productTypes, setProductTypes] = useState([]);
    const [productTypesTruncated, setProductTypesTruncated] = useState([]);
    const [productTypesListToUse, setProductTypesListToUse] = useState([]);
    const [vendorOptions, setVendorOptions] = useState([]);
    const [makeOptions, setMakeOptions] = useState([]);
    const [modelOptions, setModelOptions] = useState([]);
    const [variantOptions, setVariantOptions] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [showAllCategories, setShowAllCategories] = useState(false);
    const [showAllTypes, setShowAllTypes] = useState(false);

    const scrollTop = () => {
        window.scrollTo(0, 0);
    };

    const submitHandler = async () => {
        const searchParams = new URLSearchParams(location.search);
        if (activeFilters?.make) searchParams.set("make", activeFilters.make);
        if (activeFilters?.model) searchParams.set("model", activeFilters.model);
        if (activeFilters?.variant)
            searchParams.set("variant", activeFilters.variant);
        if (activeFilters?.year) searchParams.set("year", activeFilters.year);
        if (activeFilters?.vendor) searchParams.set("vendor", activeFilters.vendor);
        if (activeFilters?.productCategory)
            searchParams.set("product_category", activeFilters.productCategory);
        if (activeFilters?.productType)
            searchParams.set("product_type", activeFilters.productType);

        await updateQueryParameters(searchParams.toString());
    };

    const clearFilters = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const paramsToClear = ["make", "model", "variant", "year", "vendor"];
        paramsToClear.forEach((param) => {
            urlParams.delete(param);
        });
        window.location.href = `${
            window.location.pathname
        }?${urlParams.toString()}`;
    };

    const handleFilterChange = async (e) => {
        const activeFiltersCopy = cloneDeep(activeFilters);
        if (e.target.checked) {
            activeFiltersCopy[e.target.name] = e.target.value;
        } else {
            activeFiltersCopy[e.target.name] = null;
        }

        if (e.target.name === "make") {
            activeFiltersCopy.model = null;
            activeFiltersCopy.variant = null;
            activeFiltersCopy.year = null;
        } else if (e.target.name === "model") {
            activeFiltersCopy.variant = null;
            activeFiltersCopy.year = null;
        } else if (e.target.name === "variant") {
            activeFiltersCopy.year = null;
        }

        const searchParams = new URLSearchParams();
        if (activeFiltersCopy?.make)
            searchParams.set("make", activeFiltersCopy.make);
        if (activeFiltersCopy?.model)
            searchParams.set("model", activeFiltersCopy.model);
        if (activeFiltersCopy?.variant)
            searchParams.set("variant", activeFiltersCopy.variant);
        if (activeFiltersCopy?.year)
            searchParams.set("year", activeFiltersCopy.year);
        if (activeFiltersCopy?.vendor)
            searchParams.set("vendor", activeFiltersCopy.vendor);

        window.location.href = `${location.pathname}${
            searchParams.size > 0 ? "?" : ""
        }${searchParams.toString()}`;
    };

    const showMoreCategories = () => {
        setShowAllCategories(true);
        setProductCategoriesListToUse(productCategories);
    };

    const showLessCategories = () => {
        setShowAllCategories(false);
        setProductCategoriesListToUse(productCategoriesTruncated);
    };

    const showMoreTypes = () => {
        setShowAllTypes(true);
        setProductTypesListToUse(productTypes);
    };

    const showLessTypes = () => {
        setShowAllTypes(false);
        setProductTypesListToUse(productTypesTruncated);
    };

    const changePageCategory = (value) => {
        window.location.href = `/products/${value}`;
    };

    const changePageProductType = (value) => {
        const currentPath = location.pathname;
        const pathParts = split(currentPath, "/");
        const category = pathParts[2];
        window.location.href = `/products/${category}/${encodeURIComponent(value)}`;
    };

    const clearPageProductType = () => {
        const currentPath = location.pathname;
        const pathParts = split(currentPath, "/");
        const category = pathParts[2];
        window.location.href = `/products/${category}`;
    };

    const handleActiveCategoryChange = (categoryId) => {
        window.location.href = `/products/${categoryId}`;
    };

    useEffect(() => {
        scrollTop();
    }, []);

    useEffect(() => {
        let productCategoryOptions = [];

        if (categoryFilters && size(categoryFilters)) {
            productCategoryOptions = categoryFilters
                .filter((c) => c)
                .map((c) => ({
                    key: c.productCategoryId,
                    text: startCase(toLower(c.name)),
                    value: c.productCategoryId,
                }))
                .sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
            setProductCategories(productCategoryOptions);
            setProductCategoriesTruncated(slice(productCategoryOptions, 0, 3));
            setProductCategoriesListToUse(slice(productCategoryOptions, 0, 3));
        }
    }, [categoryFilters]);

    useEffect(() => {
        let productTypeOptions = [];

        if (typeFilters && size(typeFilters)) {
            productTypeOptions = typeFilters
                .filter((c) => c)
                .map(c => ({
                    key: c.productTypeId,
                    text: startCase(toLower(c.name)),
                    value: c.productTypeId,
                }))
                .sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
            setProductTypes(productTypeOptions);
            setProductTypesTruncated(slice(productTypeOptions, 0, 3));
            setProductTypesListToUse(slice(productTypeOptions, 0, 3));
        }
    }, [typeFilters]);

    useEffect(() => {
        let vendorOptions = [];

        if (vendorFilters && size(vendorFilters)) {
            vendorOptions = vendorFilters
                .filter((c) => c)
                .map(v => ({
                    key: v.vendorId,
                    text: startCase(toLower(v.name)),
                    value: v.vendorId,
                }))
                .sort((a, b) => (a.text < b.text ? -1 : a.text > b.text ? 1 : 0));
            setVendorOptions(vendorOptions);
        }
    }, [vendorFilters]);

    useEffect(() => {
        let makeOptions = [];

        if (makeFilters && size(makeFilters)) {
            makeOptions = makeFilters
                .filter((c) => c)
                .map(m => ({
                    key: m.makeId,
                    text: m.name,
                    value: m.makeId,
                }))
                .sort((a, b) => (a.text < b.text ? -1 : a.text > b.text ? 1 : 0));
            setMakeOptions(makeOptions);
        }
    }, [makeFilters]);

    useEffect(() => {
        let modelOptions = [];

        if (modelFilters && size(modelFilters)) {
            modelOptions = modelFilters
                .filter((c) => c)
                .map(m => ({
                    key: m.modelId,
                    text: m.name,
                    value: m.modelId,
                }))
                .sort((a, b) => (a.text < b.text ? -1 : a.text > b.text ? 1 : 0));
            setModelOptions(modelOptions);
        }
    }, [modelFilters]);

    useEffect(() => {
        let variantOptions = [];

        if (variantFilters && size(variantFilters)) {
            variantOptions = variantFilters
                .filter((c) => c)
                .map(m => ({
                    key: m.variantId,
                    text: m.name,
                    value: m.variantId,
                }))
                .sort((a, b) => (a.text < b.text ? -1 : a.text > b.text ? 1 : 0));
            setVariantOptions(variantOptions);
        }
    }, [variantFilters]);

    useEffect(() => {
        let yearOptions = [];

        if (yearFilters && size(yearFilters)) {
            yearOptions = yearFilters
                .filter((c) => c)
                .map((year) => ({
                    key: year.yearId,
                    text: year.name,
                    value: year.yearId,
                }))
                .sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
            setYearOptions(yearOptions);
        }
    }, [yearFilters]);

    return (
        <>
            {isMobile ? (
                <MobileProductGrid
                    products={products}
                    pricingData={pricingData}
                    stockInfo={stockInfo}
                    customerCategory={customerCategory}
                    categoriesAndTypes={categoriesAndTypes}
                    activeVendor={activeVendor}
                    activeFilters={activeFilters}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    total={total}
                    handlePageNumberChange={handlePageNumberChange}
                    updateQueryParameters={updateQueryParameters}
                    vendorOptions={vendorOptions}
                    makeOptions={makeOptions}
                    modelOptions={modelOptions}
                    variantOptions={variantOptions}
                    yearOptions={yearOptions}
                    productCategoriesListToUse={productCategoriesListToUse}
                    productTypesListToUse={productTypesListToUse}
                    showAllCategories={showAllCategories}
                    showAllTypes={showAllTypes}
                    showMoreCategories={showMoreCategories}
                    showLessCategories={showLessCategories}
                    showMoreTypes={showMoreTypes}
                    showLessTypes={showLessTypes}
                    handleFilterChange={handleFilterChange}
                    changePageCategory={changePageCategory}
                    changePageProductType={changePageProductType}
                    clearPageProductType={clearPageProductType}
                    handleActiveCategoryChange={handleActiveCategoryChange}
                    isMobile={isMobile}
                    filtersLoading={filtersLoading}
                />
            ) : (
                <DesktopProductGrid
                    products={products}
                    pricingData={pricingData}
                    stockInfo={stockInfo}
                    customerCategory={customerCategory}
                    categoriesAndTypes={categoriesAndTypes}
                    activeVendor={activeVendor}
                    activeFilters={activeFilters}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    total={total}
                    handlePageNumberChange={handlePageNumberChange}
                    updateQueryParameters={updateQueryParameters}
                    vendorOptions={vendorOptions}
                    makeOptions={makeOptions}
                    modelOptions={modelOptions}
                    variantOptions={variantOptions}
                    yearOptions={yearOptions}
                    productCategoriesListToUse={productCategoriesListToUse}
                    productTypesListToUse={productTypesListToUse}
                    showAllCategories={showAllCategories}
                    showAllTypes={showAllTypes}
                    showMoreCategories={showMoreCategories}
                    showLessCategories={showLessCategories}
                    showMoreTypes={showMoreTypes}
                    showLessTypes={showLessTypes}
                    handleFilterChange={handleFilterChange}
                    changePageCategory={changePageCategory}
                    changePageProductType={changePageProductType}
                    clearPageProductType={clearPageProductType}
                    handleActiveCategoryChange={handleActiveCategoryChange}
                    isMobile={isMobile}
                    filtersLoading={filtersLoading}
                />
            )}
        </>
    );
};

export default ProductGrid;
